import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintIcon from "@mui/icons-material/Print";
import "../../FrontOffice/OutPatients/OutpatientHome.css"

import {
    AppointmentCard,
    CommonGridToolBarWithFilterText,
    CommonPatientDetails,
} from "../../../Components/Common Components/CommonComponents";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";
import { AmountFormat, ContentInTooltip, formatDate } from "../../../Components/CommonFunctions/CommonFunctions";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { renderIconButton } from "../../Doctor/DoctorNotes/Common/DocCommonComponent";
import PaymentsIcon from '@mui/icons-material/Payments';
import ReportIcon from '@mui/icons-material/Report';
import { useHistory } from "react-router-dom";


const formatTherapists = (therapists) => {
    if (!therapists || !Array.isArray(therapists) || therapists.length === 0) return { display: "-", full: "-" };
    const names = therapists.map(t => t.therapist_name || t.name || "").filter(Boolean);
    const full = names.join(", ");
    return { display: full, full };
};
const formatTreatments = (treatments) => {
    if (!treatments || !Array.isArray(treatments) || treatments.length === 0) return { display: "-", full: "-" };
    const names = treatments.map(t => t.treatment_name || t.name || "").filter(Boolean);
    const full = names.join(", ");
    return { display: full, full };
};
const formatDuration = (treatments) => {
    if (!treatments || !Array.isArray(treatments) || treatments.length === 0) return "-";
    const durations = treatments.map(t => `${t.duration || 0} ${t.duration_mode || "Mins"}`).filter(Boolean);
    return durations.length > 0 ? durations.join(", ") : "-";
};

const TherapistHome = (props) => {
    const history = useHistory();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusCount, setStatusCount] = useState({});
    const [menuRow, setMenuRow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [filterDate, setFilterDate] = useState(new Date());
    const [admissionType, setAdmissionType] = useState("Walkin"); // Walkin | IP
    const [selectedStatus, setSelectedStatus] = useState("");
    const [searchKey, setSearchKey] = useState("");
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [printLoading, setPrintLoading] = useState(false);
    const pageSizeOptions = [10, 20, 30];

    const errorMessage = (msg) => {
        seterrorMsgText(msg)
        seterrorMsg(true)
        setIsLoading(false)
    }

    const successMessage = (msg) => {
        setsuccessMsgText(msg)
        setsuccessMsg(true)
        setIsLoading(false)
    }

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    useEffect(() => {
        fetchAppointments();
    }, [filterDate, selectedStatus, admissionType]);

    const fetchAppointments = useCallback(() => {
        setIsLoading(true);
        try {
            const appointmentDate = filterDate ? dayjs(filterDate).format("YYYY-MM-DD") : "";
            const status = selectedStatus || "";
            const qs = `?appointment_date=${appointmentDate}&status=${status}&admission_type=${admissionType}&therapist_home='true'`;
            RestAPIService.getAll(Serviceurls.AYURVEDA_THERAPIST_GET + qs)
                .then((res) => {
                    if (res.data.status === "success") {
                        const raw = res.data.data || [];
                        const therapists = (row) => {
                            if (!row.therapists || !Array.isArray(row.therapists)) return { display: "-", full: "-" };
                            const names = row.therapists.map(t => t.therapist_name || t.name || "").filter(Boolean);
                            return { display: names.join(", "), full: names.join(", ") };
                        };
                        const treatments = (row) => {
                            if (!row.treatments || !Array.isArray(row.treatments)) return { display: "-", full: "-" };
                            const names = row.treatments.map(t => t.treatment_name || t.name || "").filter(Boolean);
                            return { display: names.join(", "), full: names.join(", ") };
                        };
                        const list = raw.map((row, idx) => {
                            const tr = treatments(row);
                            const dur = formatDuration(row.treatments);
                            const startTime = row.start_time ? row.start_time : "-";
                            return {
                                ...row,
                                sno: idx + 1,
                                patient_name: row.patient_name?.trim() || "",
                                patient_age: row.patient_age || "",
                                patient_mobile_number: row.patient_mobile_number || "",
                                patient_account_number: row.patient_account_number || "",
                                doctor_name: row.doctor_name || "-",
                                therapy_name: tr.display,
                                therapy_duration: dur,
                                therapy_time: startTime,
                                status: row.status || "Pending",
                                VastiStatus: row.is_vasti ? "Yes" : "No",
                                therpistCount: row.therapists?.length || 0,
                            };
                        });
                        setAppointments(list);
                        setStatusCount(res.data || {});
                    } else {
                        errorMessage(res.data.message || "Failed to fetch appointments");
                    }
                })
                .catch((e) => errorMessage(e?.response?.data?.message || e.message))
                .finally(() => setIsLoading(false));
        } catch (e) {
            setIsLoading(false);
            errorMessage(e.message);
        }
    }, [filterDate, selectedStatus, admissionType]);

    // Update API (cancel / reschedule) using updateWithOutId pattern

    const onCardClick = (statusKey) => {
        setSelectedStatus(statusKey);
    };

    const handleTablePrint = () => {
        const appointmentDate = filterDate ? dayjs(filterDate).format("YYYY-MM-DD") : "";
        const status = selectedStatus || "";
        const qs = `?appointment_date=${appointmentDate}&status=${status}&admission_type=${admissionType}&export_type=pdf`;
        setPrintLoading(true);
        RestAPIService.getAll(Serviceurls.AYURVEDA_THERAPIST_GET + qs)
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                window.open(URL.createObjectURL(file));
            })
            .catch((e) => errorMessage(e?.response?.data?.message || e?.message || "Print failed"))
            .finally(() => setPrintLoading(false));
    };

    const confirmCancel = () => {
        if (!selectedToCancel) {
            setCancelDialogOpen(false);
            return;
        }
        const payload = {
            status: "Cancelled",
            appointment_id: selectedToCancel?.id,
        };
        updateAppointment(payload);
        setCancelDialogOpen(false);
    };

    const columns = [
        {
            field: "patient_name",
            headerName: "Patient's Details",
            flex: 0.3,
            renderCell: (params) => (
                <Box>
                    <CommonPatientDetails data={params.row} />
                </Box>
            ),
            headerAlign: "left",
            align: "left",
        },
        {
            field: "therapy_name",
            headerName: "Therapy Name",
            flex: 0.18,
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.9vw" }}>
                    {params.row?.therapy_name || "-"}
                </Typography>
            ),
        },
        {
            field: "patient_type",
            headerName: "Patient Type",
            flex: 0.2,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box>
                    <Typography sx={{ fontSize: "0.9vw" }}>
                        {params.row?.patient_type || "-"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "therapy_time",
            headerName: "Therapy Time",
            flex: 0.18,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.9vw" }}>
                    {params.row?.therapy_time || "-"}
                </Typography>
            ),
        },
        {
            field: "room_name",
            headerName: "Therapy Room",
            flex: 0.2,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box>
                    <Typography sx={{ fontSize: "0.9vw" }}>
                        {params.row?.room_name || "-"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "VastiStatus",
            headerName: "Vasti Status",
            flex: 0.10,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography sx={{ fontSize: ".85vw", fontWeight: "bold", color: params.row?.is_vasti ? "green" : "red" }}>
                    {params.row?.VastiStatus || '-'}
                </Typography>
            ),
        },
        {
            field: "therpistCount",
            headerName: "Therapist Count",
            flex: 0.15,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box>
                    <Typography sx={{ fontSize: "0.9vw" }}>
                        {params.row?.therpistCount || "-"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.2,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const status = params.row?.status;
                const color =
                    status === "Completed"
                        ? "#008c4b"
                        : status === "Medication Inprogress"
                            ? "#0461B7"
                            : status === "Medication Ready"
                                ? "#0d9937"
                                : status === "Arrived" ?
                                    "#0484bf"
                                    : status === "Cancelled"
                                        ? "#e10303db"
                                        : "#ef8b1b";
                return (
                    <Box
                        sx={{
                            px: 1,
                            py: 1,
                            borderRadius: "3px",
                            backgroundColor: color,
                            color: "white",
                            textAlign: "center",
                            fontSize: "0.9vw",
                            width: "10vw",
                        }}
                    >
                        {status}
                    </Box>
                );
            },
        },
        {
            field: "action",
            headerName: "Action",
            flex: 0.1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                    <Tooltip title="Therapist Session Details">
                        <IconButton
                            disabled={params.row.status === "Medication Ready" || params.row.status === "Stopped" || params.row.status === "Started" ? false : true}
                            size="small"
                            onClick={() => history.push("/AyurvedaTherapist/TherapistSession", { id: params.row.id, row: params.row, APIDate: filterDate, APIStatus: selectedStatus || '' })}
                            sx={{ color: params.row.status === "Medication Ready" || params.row.status === "Stopped" || params.row.status === "Started" ? "#04B7B1" : "#999" }}
                        >
                            <VisibilityIcon sx={{ fontSize: "1.4vw" }} />
                        </IconButton>
                    </Tooltip>
                    {/* <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setMenuRow(params.row); setAnchorEl(e.currentTarget); }}
                        sx={{ color: "#666" }}
                    >
                        <MoreVertIcon sx={{ fontSize: "1.4vw" }} />
                    </IconButton> */}
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ height: "85vh", width: "100%", backgroundColor: Colors.Background, p: 1 }}>
            {/* Header area: date picker & cards */}
            <div className="eMed_OP_AptHead" style={{ justifyContent: "space-between" }}>
                <Typography variant="h6" ml={2} fontWeight={500}>Therapist Home</Typography>
                <div className='eMed_Header_OP_home_div' style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ButtonGroup
                        size="small"
                        disableElevation
                        sx={{
                            "& .MuiButton-root": { textTransform: "capitalize", minWidth: 72, px: 1.5, borderColor: "divider" },
                            "& .MuiButtonGroup-grouped:not(:last-of-type)": { borderColor: "divider" },
                        }}
                    >
                        <Button
                            variant={admissionType === "Walkin" ? "contained" : "outlined"}
                            onClick={() => setAdmissionType("Walkin")}
                            sx={{ fontWeight: admissionType === "Walkin" ? 600 : 400 }}
                        >
                            Walk-in
                        </Button>
                        <Button
                            variant={admissionType === "IP" ? "contained" : "outlined"}
                            onClick={() => setAdmissionType("IP")}
                            sx={{ fontWeight: admissionType === "IP" ? 600 : 400 }}
                        >
                            IP
                        </Button>
                    </ButtonGroup>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={filterDate ? filterDate : null}
                            name={"datePicker"} autoFocus={false}
                            inputFormat='DD-MM-YYYY'
                            views={["year", "month", "day"]}
                            onChange={(newDate) => {
                                setFilterDate(newDate);
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                label={"Date"}
                                variant="outlined"
                                onKeyDown={(e) => e.preventDefault()}
                                sx={{ width: '15dvw', marginLeft: '1vw' }}
                                size='small'
                            />}
                        />
                    </LocalizationProvider>
                    <Tooltip title="Print" placement="top" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleTablePrint}
                                disabled={!appointments?.length || printLoading}
                            >
                                <PrintIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>
            <Box display="flex" justifyContent="end" alignItems="center">
                <Box display="flex" gap={1}>
                    <AppointmentCard
                        onClick={() => onCardClick("")}
                        count={statusCount?.total_count || 0}
                        label={"All"}
                        color={"#04B7B1"}
                        isSelectedCard={selectedStatus === "" || selectedStatus === null}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Pending")}
                        count={statusCount?.pending_count || 0}
                        label={"Pending"}
                        color={"#B87333"}
                        isSelectedCard={selectedStatus === "Pending"}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Medication Ready")}
                        count={statusCount?.medication_ready_count || 0}
                        label={"Medicine Ready"}
                        color={"#0461B7"}
                        isSelectedCard={selectedStatus === "Medication Ready"}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Completed")}
                        count={statusCount?.completed_count || 0}
                        label={"Completed"}
                        color={"#008c4b"}
                        isSelectedCard={selectedStatus === "Completed"}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Cancelled")}
                        count={statusCount?.cancelled_count || 0}
                        label={"Cancelled"}
                        color={"#ff0000"}
                        isSelectedCard={selectedStatus === "Cancelled"}
                    />
                </Box>
            </Box>

            {/* Data Grid area */}
            <Box sx={{ height: '64vh', backgroundColor: 'white' }}>
                <DataGrid
                    rows={appointments || []}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pageSizeOptions={pageSizeOptions}
                    pagination
                    paginationMode="client"
                    paginationModel={{
                        page: page,
                        pageSize: pageSize
                    }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page)
                        setPageSize(model.pageSize)
                    }}
                    density="comfortable"
                    headerHeight={40}
                    rowHeight={45}
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            minHeight: 35,
                            maxHeight: 35,
                        },
                    }}
                    components={{
                        Toolbar: () => (
                            <Box sx={{ p: 0.5 }}>
                                <CommonGridToolBarWithFilterText />
                            </Box>
                        ),
                        NoRowsOverlay: () => (
                            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                                No Records To Be Shown
                            </Box>
                        ),
                    }}
                />
            </Box>
            {errorMsg ?
                <ToastMsg
                    severity={'error'}
                    msg={errorMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
            {successMsg ?
                <ToastMsg
                    severity={'success'}
                    msg={successMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
            {/* <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={() => { handleMenuClose(); if (menuRow) history.push("/Ayurveda/MedicinePreparation", { id: menuRow.id, row: menuRow }); }}>Medicine Preparation</MenuItem>
            </Menu> */}
            {isLoading && <Loader />}
        </Box>
    );
};

export default TherapistHome;